import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ROUTES } from "../../../../routes/routes";
import { RoleEnum } from "../../../../api/client/types/user.types";
import { useAuthStore } from "../../../../store";
import {
  useGetSwimmerById,
  useDeleteSwimmer,
  swimmerQueryKeys,
} from "../../services/swimmerService";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";

export const useDeleteSwimmerPage = () => {
  const { swimmerId } = useParams<{ swimmerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [hasDeleted, setHasDeleted] = useState(false);

  const {
    data: swimmer,
    isLoading: isLoadingSwimmer,
    isError: isErrorSwimmer,
  } = useGetSwimmerById(swimmerId ?? "", {
    enabled: !!swimmerId && !hasDeleted,
  });

  const { mutate: deleteSwimmer, isPending: isDeleting } = useDeleteSwimmer();

  const handleNavigateToDashboard = () => {
    const isLifeguard = user?.role === RoleEnum.LIFEGUARD;
    if (isLifeguard) {
      navigate(`${ROUTES.DASHBOARD.LIFEGUARD}?tab=banistas`);
      return;
    }

    const returnSearch =
      (location.state as { returnSearch?: string } | undefined)?.returnSearch ??
      "?tab=banistas";
    navigate(ROUTES.DASHBOARD.ADMINISTRATOR + returnSearch);
  };

  const invalidateOrUpdateListCache = () => {
    const listQueryKey = [...swimmerQueryKeys.root, "list"];
    const isLifeguard = user?.role === RoleEnum.LIFEGUARD;

    if (isLifeguard) {
      // For lifeguard, update the default list in cache (first page, own building)
      queryClient.setQueriesData<PaginatedResponse<Swimmer>>(
        { queryKey: listQueryKey, exact: false },
        (old) => {
          if (!old?.items || !swimmerId) return old;
          return {
            ...old,
            items: old.items.filter((s) => s.id !== swimmerId),
          };
        },
      );
    } else {
      // Admin: just invalidate so tables and previews refetch with current filters
      queryClient.invalidateQueries({ queryKey: listQueryKey });
      queryClient.invalidateQueries({
        queryKey: [...swimmerQueryKeys.root, "reportPreview"],
      });
    }
  };

  /**
   * When the deleted swimmer was a dependent of another swimmer: (1) optimistically
   * remove them from that responsible's detail cache, and (2) invalidate that detail
   * so the next time the responsible's page is opened it refetches and shows correct dependents.
   */
  const updateResponsibleDetailAfterDependentDeleted = () => {
    const responsibleId =
      swimmer?.responsible_id ?? swimmer?.responsible?.id ?? undefined;
    if (!responsibleId || !swimmerId) return;

    const detailKey = swimmerQueryKeys.detail(responsibleId);

    // Optimistic update: if responsible's detail is already in cache, remove the deleted dependent
    queryClient.setQueryData<Swimmer>(detailKey, (old) => {
      if (!old?.dependents) return old;
      return {
        ...old,
        dependents: old.dependents.filter((d) => d.id !== swimmerId),
      };
    });
  };

  const handleConfirmDelete = () => {
    if (!swimmerId) return;

    deleteSwimmer(
      { swimmer_id: swimmerId },
      {
        onSuccess: () => {
          setHasDeleted(true);
          invalidateOrUpdateListCache();
          updateResponsibleDetailAfterDependentDeleted();
          queryClient.removeQueries({
            queryKey: swimmerQueryKeys.detail(swimmerId),
          });
          toast.success("Bañista eliminado correctamente.");
          handleNavigateToDashboard();
        },
      },
    );
  };

  return {
    swimmerId,
    swimmer,
    isLoadingSwimmer,
    isErrorSwimmer,
    isDeleting,
    handleConfirmDelete,
    handleNavigateToDashboard,
  };
};
